import React, { useMemo } from 'react';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import ViewEditModalHeader from '../../../../../components/modal/ViewEditModalHeader';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import LeaseDataTable from './LeaseDataTable';

function formatNumber(number, decimals = 2) {
	if (typeof number !== 'number' || isNaN(number)) {
		// Return an empty string or '0.00' for invalid inputs to avoid breaking the UI
		return '';
	}
	const roundedNumber = Number(number.toFixed(decimals));
	const formatter = new Intl.NumberFormat('en-US', {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
	return formatter.format(roundedNumber);
}

const LeaseRecordViewModal = ({ showModal, setShowModal, checkResponseData = [], tableUpperLeftExtraInfo = null, isDataLoading }) => {
	// console.log(checkResponseData,'checkResponseData123');

	// -------------------- CHANGE 1: OPTIMIZED DATA PROCESSING WITH useMemo --------------------
	// Instead of using useEffect to set state, we compute the processed data and the `showGlData` flag directly with useMemo.
	// This makes the data flow more declarative and efficient.
	const { propcessData, showGlData } = useMemo(() => {
		let arrayExcludeData = [];
		let hasGlData = false;

		const filteredData = checkResponseData?.filter((item) => item?.group !== 'Payment') || [];

		filteredData.forEach((item) => {
			if (item?.glData) {
				hasGlData = true;
			}

			if (item?.ccList?.length > 0) {
				item.ccList.forEach((ccItem) => {
					let itemData = {
						date: item?.entryDate?.dateString ? dayjs(item.entryDate.dateString).format('YYYY-MM-DD') : '',
						head: item?.head || '',
						description: item?.description || '',
						debit: '',
						credit: '',
						costCenterCode: ccItem.headCode || '',
						costCenterDescription: ccItem.head || '',
						headCode: item?.headCode || '',
						glAccount: item?.glData?.generalLedgerCode || '',
						glAccountDescription: item?.glData?.generalLegerDescription || '',
						headGroup: item?.headGroup || '',
					};

					if (ccItem?.debit && !isNaN(ccItem.debit)) {
						itemData.debit = formatNumber(ccItem.debit);
					}
					if (ccItem?.credit && !isNaN(ccItem.credit)) {
						itemData.credit = formatNumber(ccItem.credit);
					}
					arrayExcludeData.push(itemData);
				});
			} else {
				let itemData = {
					date: item?.entryDate?.dateString ? dayjs(item.entryDate.dateString).format('YYYY-MM-DD') : '',
					head: item?.head || '',
					description: item?.description || '',
					debit: '',
					credit: '',
					costCenterCode: '',
					costCenterDescription: '',
					headCode: item?.headCode || '',
					glAccount: item?.glData?.generalLedgerCode || '',
					glAccountDescription: item?.glData?.generalLegerDescription || '',
					headGroup: item?.headGroup || '',
				};

				if (item?.debit && !isNaN(item.debit)) {
					itemData.debit = formatNumber(item.debit);
				}
				if (item?.credit && !isNaN(item.credit)) {
					itemData.credit = formatNumber(item.credit);
				}
				arrayExcludeData.push(itemData);
			}
		});

		return { propcessData: arrayExcludeData, showGlData: hasGlData };
	}, [checkResponseData]);

	const checkColumnDesign = useMemo(
		() => [
			// ... (your column definitions remain the same)
			{
				id: 'date',
				width: '5%',
				align: 'center',
				accessor: (data) => (data?.date ? dayjs(data.date).format('YYYY-MM-DD') : ''),
				cellAlign: 'center',
				Header: 'Date',
				Cell: (cell) => (
					<Typography align="center">{cell?.row?.original?.date ? dayjs(cell.row.original.date).format('YYYY-MM-DD') : ''}</Typography>
				),
			},
			{
				id: 'costCenterCode',
				width: '5%',
				align: 'center',
				accessor: 'costCenterCode',
				cellAlign: 'left',
				Header: 'Cost Center',
				// isVisible: showGlData,
				isVisible: false,
				// Cell: (cell) => <Typography>{cell?.row?.original?.costCenterCode}</Typography>,
			},
			{
				id: 'costCenterDescription',
				width: '5%',
				align: 'center',
				accessor: 'costCenterDescription',
				cellAlign: 'left',
				Header: 'Cost Center Description',
				// isVisible: showGlData,
				isVisible: false,
				// Cell: (cell) => <Typography>{cell?.row?.original?.costCenterDescription}</Typography>,
			},
			{
				id: 'glAccount',
				width: '5%',
				align: 'center',
				accessor: 'glAccount',
				cellAlign: 'left',
				Header: 'GL Account',
				// isVisible: showGlData,
				isVisible: false,
				// Cell: (cell) => <Typography>{cell?.row?.original?.glAccount}</Typography>,
			},
			{
				id: 'glAccountDescription',
				width: '5%',
				align: 'center',
				accessor: 'glAccountDescription',
				cellAlign: 'left',
				Header: 'GL Account Description',
				// isVisible: showGlData,
				isVisible: false,
				// Cell: (cell) => <Typography>{cell?.row?.original?.glAccountDescription}</Typography>,
			},
			{
				id: 'leaserHead',
				width: '5%',
				align: 'center',
				accessor: 'headGroup',
				cellAlign: 'left',
				Header: 'Lease Heads',
				// isVisible: !showGlData,
				// Cell: (cell) => <Typography>{cell?.row?.original?.headGroup}</Typography>,
			},
			{
				id: 'description',
				width: '5%',
				align: 'center',
				accessor: 'description',
				cellAlign: 'left',
				Header: 'Description',
				// Cell: (cell) => <Typography>{cell?.row?.original?.description}</Typography>,
			},
			{
				id: 'debit',
				width: '5%',
				align: 'center',
				accessor: (data) => (data?.debit && +data?.debit !== 0 ? data?.debit : ''),
				cellAlign: 'center',
				Header: 'Debit',
				Cell: (cell) => (
					<Typography align="right">
						{cell?.row?.original?.debit && +cell?.row?.original?.debit !== 0 ? cell?.row?.original?.debit : ''}
					</Typography>
				),
			},
			{
				id: 'credit',
				width: '5%',
				align: 'center',
				accessor: (data) => (data?.credit && +data?.credit !== 0 ? data?.credit : ''),
				cellAlign: 'center',
				Header: 'Credit',
				Cell: (cell) => (
					<Typography align="right">
						{cell?.row?.original?.credit && +cell?.row?.original?.credit !== 0 ? cell?.row?.original?.credit : ''}
					</Typography>
				),
			},
		],
		[showGlData] // Dependency is now clearer
	);

	const defaultVisibleColumn = useMemo(() => {
		return checkColumnDesign.filter((column) => column.isVisible === false).map((column) => column.id);
	}, [checkColumnDesign]);

	const handleClose = () => {
		setShowModal(false);
	};

	// -------------------- CHANGE 2: RESTRUCTURED RENDER LOGIC --------------------
	// This single function now handles all three states: loading, empty, and data available.
	const renderContent = () => {
		if (isDataLoading) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
					<Spin size="large" tip="Loading..." />
				</Box>
			);
		}

		if (!propcessData || propcessData.length === 0) {
			return (
				<Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} flexDirection="column">
					<Typography variant="h6" color="textSecondary" gutterBottom>
						No Data Available
					</Typography>
					<Typography variant="body2" color="textSecondary">
						There are no journal entries to display.
					</Typography>
				</Box>
			);
		}

		return (
			<LeaseDataTable
				xlsxFileName={`${tableUpperLeftExtraInfo?.leaseId}_date`}
				firstColStrick={false}
				COLUMNS={checkColumnDesign}
				DATA={propcessData}
				pageSize={propcessData.length}
				pagination={false}
				onClickHandler={() => {}}
				columnFilterSelector={true}
				tableUpperLeftExtraInfo={tableUpperLeftExtraInfo}
				defaultVisibleColumn={defaultVisibleColumn}
			/>
		);
	};

	return (
		<React.Fragment>
			<Dialog
				fullWidth={true}
				maxWidth={'lg'}
				open={showModal}
				keepMounted
				onClose={handleClose}
				aria-labelledby="draggable-dialog-title"
				sx={{ '& .MuiPaper-root': { borderRadius: '8px' } }}
			>
				<ViewEditModalHeader
					headerTitle="Journal Entry"
					handleClose={handleClose}
					isScreenSize={false}
					icon={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
				/>

				<DialogContent sx={{ pb: { xs: 4, md: 5 } }}>
					{/* -------------------- CHANGE 3: ADDED STABLE CONTAINER -------------------- */}
					{/* This Box provides a stable frame with a minimum height, preventing the modal from resizing. */}
					<Box
						sx={{
							// minHeight: '450px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						{renderContent()}
					</Box>
				</DialogContent>
			</Dialog>
		</React.Fragment>
	);
};
export default LeaseRecordViewModal;
